import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  CalendarDaysIcon,
  BellAlertIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const SCHEME_OPTIONS = [
  { value: "", label: "All (REPNFT + UNSREP)" },
  { value: "REPNFT", label: "REPNFT" },
  { value: "UNSREP", label: "UNSREP" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "upcoming", label: "Upcoming (Active)" },
  { value: "matured", label: "Matured / Past" },
];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const MaturityBadge = ({ days, status }) => {
  if (status === "Returned") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <CheckCircleIcon className="h-3 w-3 mr-1" /> Returned
      </span>
    );
  }
  if (days === null || days === undefined) return null;
  if (days < 0) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
        Overdue {Math.abs(days)}d
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        <BellAlertIcon className="h-3 w-3 mr-1" /> Due in {days}d
      </span>
    );
  }
  if (days <= 90) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <ClockIcon className="h-3 w-3 mr-1" /> {days}d
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      {days}d
    </span>
  );
};

const MaturityPage = () => {
  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({
    year: String(currentYear),
    month: "",
    scheme: "",
    status: "all",
    search: "",
  });

  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [page, setPage] = useState(1);

  const [monthly, setMonthly] = useState([]);
  const [notifications, setNotifications] = useState({
    counts: { total: 0, upcoming: 0, overdue: 0 },
    upcoming: [],
    overdue: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExportOpen(false);
    setExporting(true);
    try {
      const params = {
        format,
        status: filters.status,
        search: filters.search,
      };
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;
      if (filters.scheme) params.scheme = filters.scheme;

      const response = await api.get("/api/maturity/export", {
        params,
        responseType: "blob",
      });

      const ext = format === "xlsx" ? "xlsx" : format;
      const mime =
        format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : format === "json"
          ? "application/json"
          : "text/csv";
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `maturity_export_${ts}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export maturity data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const fetchList = useCallback(async () => {
    try {
      const params = {
        page,
        limit: 50,
        status: filters.status,
        search: filters.search,
      };
      if (filters.year) params.year = filters.year;
      if (filters.month) params.month = filters.month;
      if (filters.scheme) params.scheme = filters.scheme;

      const { data } = await api.get("/api/maturity", { params });
      setList(data.data || []);
      setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
    } catch (err) {
      console.error("Failed to fetch maturity list", err);
      setError(err.response?.data?.message || "Failed to load maturity data");
    }
  }, [filters, page]);

  const fetchMonthly = useCallback(async () => {
    try {
      const params = {};
      if (filters.year) params.year = filters.year;
      const { data } = await api.get("/api/maturity/monthly-summary", { params });
      setMonthly(data.data || []);
    } catch (err) {
      console.error("Failed to fetch monthly summary", err);
    }
  }, [filters.year]);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/api/maturity/notifications", {
        params: { days: 30 },
      });
      setNotifications(data);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([fetchList(), fetchMonthly(), fetchNotifications()]).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [fetchList, fetchMonthly, fetchNotifications]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const yearsList = useMemo(() => {
    const years = new Set([currentYear, currentYear + 1, currentYear + 2]);
    monthly.forEach((m) => years.add(Number(m.year)));
    return Array.from(years).sort((a, b) => a - b);
  }, [monthly, currentYear]);

  const monthlyByMonth = useMemo(() => {
    const map = new Map();
    monthly.forEach((m) => map.set(Number(m.month), m));
    return map;
  }, [monthly]);

  const totalForYear = useMemo(
    () => monthly.reduce((sum, m) => sum + Number(m.total || 0), 0),
    [monthly]
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarDaysIcon className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Maturity Tracker
            </h1>
            <p className="text-sm text-gray-600">
              REPNFT &amp; UNSREP investors — maturity is purchase date + 600
              days.
            </p>
          </div>
        </div>
        <Link
          to="/dashboard"
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <HomeIcon className="h-4 w-4 mr-2" />
          Dashboard
        </Link>
      </div>

      {/* Notifications */}
      {(notifications.counts?.overdue > 0 ||
        notifications.counts?.upcoming > 0) && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-900 font-semibold">
                {notifications.counts.overdue} Overdue Maturities
              </p>
              <p className="text-xs text-red-700 mt-1">
                Active investors past their 600-day maturity date.
              </p>
            </div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start gap-3">
            <BellAlertIcon className="h-6 w-6 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-orange-900 font-semibold">
                {notifications.counts.upcoming} Maturing in Next 30 Days
              </p>
              <p className="text-xs text-orange-700 mt-1">
                Prepare returns for these investors.
              </p>
            </div>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
            <CalendarDaysIcon className="h-6 w-6 text-indigo-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-indigo-900 font-semibold">
                {totalForYear} Maturities in {filters.year || "All Years"}
              </p>
              <p className="text-xs text-indigo-700 mt-1">
                Across REPNFT and UNSREP schemes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Month-wise Summary */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Month-wise Maturity Summary
          </h2>
          <select
            value={filters.year}
            onChange={(e) => handleFilterChange("year", e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Years</option>
            {yearsList.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {MONTHS.map((label, idx) => {
            const monthNum = idx + 1;
            const data = monthlyByMonth.get(monthNum);
            const total = data ? Number(data.total) : 0;
            const isActive = String(monthNum) === String(filters.month);
            return (
              <button
                key={monthNum}
                onClick={() =>
                  handleFilterChange(
                    "month",
                    isActive ? "" : String(monthNum)
                  )
                }
                className={`text-left p-3 rounded-lg border transition-all ${
                  isActive
                    ? "bg-indigo-50 border-indigo-400 ring-2 ring-indigo-300"
                    : total > 0
                    ? "bg-white border-gray-200 hover:border-indigo-300"
                    : "bg-gray-50 border-gray-100 text-gray-400"
                }`}
              >
                <div className="text-xs font-medium text-gray-500">
                  {label} {filters.year || ""}
                </div>
                <div className="text-2xl font-bold text-gray-900">{total}</div>
                {data && (
                  <div className="text-[11px] text-gray-500 mt-1 space-x-2">
                    <span>R:{data.repnft_count}</span>
                    <span>U:{data.unsrep_count}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {filters.month && (
          <div className="px-6 py-2 border-t bg-gray-50 text-sm">
            Filtered by{" "}
            <span className="font-semibold">
              {MONTHS[Number(filters.month) - 1]} {filters.year}
            </span>
            <button
              onClick={() => handleFilterChange("month", "")}
              className="ml-2 text-blue-600 hover:underline"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Scheme
          </label>
          <select
            value={filters.scheme}
            onChange={(e) => handleFilterChange("scheme", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {SCHEME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Search
          </label>
          <div className="relative">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
            <input
              type="text"
              placeholder="Name, wallet, token, mobile..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            Investor Maturity List
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {pagination.total} record{pagination.total === 1 ? "" : "s"}
            </span>
            <div className="relative">
              <button
                onClick={() => setExportOpen((v) => !v)}
                disabled={exporting || pagination.total === 0}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                {exporting ? "Exporting..." : "Export"}
              </button>
              {exportOpen && (
                <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  <button
                    onClick={() => handleExport("csv")}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    CSV (.csv)
                  </button>
                  <button
                    onClick={() => handleExport("xlsx")}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    Excel (.xlsx)
                  </button>
                  <button
                    onClick={() => handleExport("json")}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  >
                    JSON (.json)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto"></div>
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-gray-500 text-sm">
            No maturity records match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Investor
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Scheme
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Token
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Wallet Address
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Purchase Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Maturity Date
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {list.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">
                        {row.buyer_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.mobile_number || ""}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                        {row.scheme_code}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {row.token_number || "-"}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">
                      {row.wallet_address ? (
                        <span
                          title={row.wallet_address}
                          className="break-all"
                        >
                          {row.wallet_address.length > 18
                            ? `${row.wallet_address.slice(
                                0,
                                8
                              )}…${row.wallet_address.slice(-6)}`
                            : row.wallet_address}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-4 py-2">{formatDate(row.purchase_date)}</td>
                    <td className="px-4 py-2 font-semibold text-gray-900">
                      {formatDate(row.maturity_date)}
                    </td>
                    <td className="px-4 py-2">
                      <MaturityBadge
                        days={Number(row.days_to_maturity)}
                        status={row.status}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 border-t flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.totalPages, p + 1))
                }
                disabled={page >= pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaturityPage;
